import { useState } from 'react';
import {
  ApiOutlined,
  ApartmentOutlined,
  BarChartOutlined,
  EditOutlined,
  KeyOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { DataTable, MenuThaoTac, PageHeader } from '../../../components/shared';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Empty, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { ChiTietUngDung } from '../components/ChiTietUngDung';
import { FormUngDung } from '../components/FormUngDung';
import { HopBiMatWebhook } from '../components/HopBiMatWebhook';
import { MAU_KIEM_CHU_KY, QUYEN, QUYEN_KHOA_API_DOI_TAC } from '../constants';
import { useDsUngDung, useGhiUngDung } from '../hooks/useCauHinhEkyc';
import { tinhTrangBiMat } from '../nghiep-vu/cau-hinh';
import type { UngDung, UngDungCoBiMat } from '../types';

type Hoi = { loai: 'xoay' | 'tat'; ungDung: UngDung } | null;

/**
 * Ứng dụng & khoá API (`/ekyc/ung-dung`, phân hệ `EKYC_APP`).
 *
 * Theo ADR-0085, không theo nguyên màn v3: đối tác **không** nhận `appSecret`/`signingKey` của eKYC mà dùng khoá API
 * của tenant (màn "Khoá API", ADR-0068). Màn này giữ phần v3 mà khoá API không che: tên, bật/tắt, webhook URL, bí
 * mật ký webhook, số liệu, lượt giao webhook — và một khối nói rõ đối tác xác thực thế nào, để người tích hợp không
 * đi tìm một khoá không còn tồn tại.
 *
 * Không có "Xoá ứng dụng", như v3: phiên giữ `ung_dung_id`, xoá ứng dụng là phiên cũ mất ngữ cảnh. Thôi dùng thì tắt.
 */
export function UngDungView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const dieuHuong = useDieuHuong();
  const quyen = useModulePermissions(QUYEN);
  const tieuDe = t('ekyc.ungDung.tieuDe');

  const ds = useDsUngDung();
  const ghi = useGhiUngDung();
  const [chon, setChon] = useState<string | null>(null);
  const [form, setForm] = useState<{ ungDung: UngDung | null } | null>(null);
  const [vuaCap, setVuaCap] = useState<UngDungCoBiMat | null>(null);
  const [hoi, setHoi] = useState<Hoi>(null);

  const items = ds.data?.items ?? [];
  const dangXem = items.find((u) => u.id === chon) ?? null;
  const coKhoaGoc = ds.data?.coKhoaGoc ?? true;

  const guiForm = async (du: { ma: string; ten: string; webhookUrl: string }) => {
    if (form?.ungDung) {
      await ghi.sua.mutateAsync({ id: form.ungDung.id, du: { ten: du.ten, webhookUrl: du.webhookUrl } });
      toast.success(t('ekyc.ungDung.daLuu'));
      setForm(null);
    } else {
      const u = await ghi.tao.mutateAsync(du);
      setForm(null);
      setChon(u.id);
      setVuaCap(u);
    }
  };

  const xacNhan = () => {
    if (!hoi) return;
    if (hoi.loai === 'xoay') {
      ghi.xoay.mutate(hoi.ungDung.id, { onSuccess: (u) => { setHoi(null); setVuaCap(u); } });
    } else {
      ghi.batTat.mutate({ id: hoi.ungDung.id, bat: false }, {
        onSuccess: () => { setHoi(null); toast.success(t('ekyc.ungDung.daTat', { ten: hoi.ungDung.ten })); },
      });
    }
  };
  const loiHoi = hoi?.loai === 'xoay' ? ghi.xoay.error : ghi.batTat.error;

  const cot = [
    {
      title: t('ekyc.ungDung.cotUngDung'),
      dataIndex: 'ten',
      key: 'ten',
      render: (_: unknown, u: UngDung) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-content-primary">{u.ten}</p>
          <p className="truncate font-mono text-2xs text-content-muted">{u.ma}</p>
        </div>
      ),
    },
    {
      title: t('chung.trangThai'),
      dataIndex: 'dangBat',
      key: 'dangBat',
      render: (_: unknown, u: UngDung) => (
        <Badge variant={u.dangBat ? 'success' : 'neutral'}>{u.dangBat ? t('ekyc.ungDung.dangDung') : t('ekyc.ungDung.daTatNhan')}</Badge>
      ),
    },
    {
      title: t('ekyc.ungDung.cotWebhook'),
      dataIndex: 'webhookUrl',
      key: 'webhookUrl',
      render: (_: unknown, u: UngDung) => (
        <div className="min-w-0 max-w-xs space-y-0.5">
          {u.webhookUrl ? (
            <p className="truncate font-mono text-xs text-content-secondary" title={u.webhookUrl}>{u.webhookUrl}</p>
          ) : (
            <p className="text-xs text-content-muted">{t('ekyc.ungDung.chuaDatWebhook')}</p>
          )}
          <p className="text-2xs text-content-muted">
            {tinhTrangBiMat(u) === 'chua-cap'
              ? t('ekyc.ungDung.biMatChuaCap')
              : t('ekyc.ungDung.biMatBan', { so: u.phienBanBiMat })}
          </p>
          {u.webhookThatBai > 0 && (
            <Badge variant="danger">{t('ekyc.ungDung.soLuotLoi', { so: soVN(u.webhookThatBai, locale) })}</Badge>
          )}
        </div>
      ),
    },
    {
      title: t('ekyc.ungDung.cotQuyTrinh'),
      key: 'quyTrinh',
      render: (_: unknown, u: UngDung) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-content-secondary">{u.quyTrinhMacDinh?.ten ?? KHONG_CO}</p>
          <p className="text-2xs text-content-muted">{t('ekyc.ungDung.soQuyTrinh', { so: u.soQuyTrinh })}</p>
        </div>
      ),
    },
    {
      title: t('ekyc.ungDung.cotPhien30'),
      key: 'phien',
      render: (_: unknown, u: UngDung) => (
        <div className="min-w-0">
          <p className="tabular-nums text-sm text-content-primary">{soVN(u.phien30Ngay, locale)}</p>
          <p className="whitespace-nowrap text-2xs text-content-muted">
            {u.phienCuoiLuc ? t('ekyc.ungDung.phienCuoi', { luc: ngayGioVN(u.phienCuoiLuc) }) : t('ekyc.ungDung.chuaCoPhien')}
          </p>
        </div>
      ),
    },
    {
      title: <span className="sr-only">{t('chung.thaoTac')}</span>,
      key: 'thaoTac',
      width: 56,
      render: (_: unknown, u: UngDung) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <MenuThaoTac
            tenBanGhi={u.ten}
            muc={[
              { khoa: 'xem', nhan: t('ekyc.ungDung.xemSoLieu'), icon: <BarChartOutlined />, nhom: 'xem', onChon: () => setChon(u.id) },
              {
                khoa: 'quy-trinh', nhan: t('ekyc.ungDung.moQuyTrinh'), icon: <ApartmentOutlined />, nhom: 'xem',
                hien: quyen.quyTrinhDoc, onChon: () => dieuHuong('/ekyc/quy-trinh'),
              },
              {
                khoa: 'sua', nhan: t('ekyc.ungDung.sua'), icon: <EditOutlined />, nhom: 'sua',
                hien: quyen.ungDungSua, onChon: () => setForm({ ungDung: u }),
              },
              {
                khoa: 'bi-mat',
                nhan: tinhTrangBiMat(u) === 'chua-cap' ? t('ekyc.ungDung.capBiMat') : t('ekyc.ungDung.xoayBiMat'),
                icon: <KeyOutlined />, nhom: 'sua',
                hien: quyen.ungDungSua,
                voHieu: !coKhoaGoc,
                ...(coKhoaGoc ? {} : { moTa: t('ekyc.ungDung.chuaCoKhoaGocNgan') }),
                onChon: () => tinhTrangBiMat(u) === 'chua-cap'
                  ? ghi.xoay.mutate(u.id, { onSuccess: setVuaCap, onError: (e) => toast.error(dichLoi(e)) })
                  : setHoi({ loai: 'xoay', ungDung: u }),
              },
              {
                khoa: 'bat', nhan: t('ekyc.ungDung.bat'), icon: <PlayCircleOutlined />, nhom: 'sua',
                hien: quyen.ungDungSua && !u.dangBat,
                onChon: () => ghi.batTat.mutate({ id: u.id, bat: true }, {
                  onSuccess: () => toast.success(t('ekyc.ungDung.daBat', { ten: u.ten })),
                  onError: (e) => toast.error(dichLoi(e)),
                }),
              },
              {
                khoa: 'tat', nhan: t('ekyc.ungDung.tat'), icon: <PauseCircleOutlined />, nhom: 'pha-huy',
                hien: quyen.ungDungSua && u.dangBat,
                onChon: () => setHoi({ loai: 'tat', ungDung: u }),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.dinhDanhEkyc') }, { label: tieuDe }]}
          titleIcon={<ApiOutlined className="text-primary-600" />}
          title={tieuDe}
          subtitle={t('ekyc.ungDung.moTaMan')}
          actions={quyen.ungDungTao ? (
            <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setForm({ ungDung: null })}>
              {t('ekyc.ungDung.taoUngDung')}
            </Button>
          ) : undefined}
        />

        {!coKhoaGoc && (
          <div role="note" className="flex items-start gap-2 rounded border border-warning-200 bg-warning-50 px-3 py-2.5">
            <WarningOutlined className="mt-0.5 shrink-0 text-warning-700" />
            <span className="min-w-0 text-sm text-warning-700">{t('ekyc.ungDung.chuaCoKhoaGoc')}</span>
          </div>
        )}

        <Card>
          <CardBody>
            {ds.isLoading ? (
              <div className="space-y-2">
                {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : ds.isError ? (
              <div role="alert" className="flex flex-wrap items-center gap-3 rounded border border-danger-200 bg-danger-50 px-3 py-2.5">
                <WarningOutlined className="shrink-0 text-danger-600" />
                <span className="min-w-0 text-sm text-danger-700">{t('ekyc.ungDung.loiTai', { loi: dichLoi(ds.error) })}</span>
                <Button variant="outline" size="sm" onClick={() => void ds.refetch()}>{t('chung.thuLai')}</Button>
              </div>
            ) : items.length === 0 ? (
              <Empty
                icon={ApiOutlined}
                title={t('ekyc.ungDung.rong')}
                description={t('ekyc.ungDung.rongMo')}
                action={quyen.ungDungTao ? (
                  <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setForm({ ungDung: null })}>
                    {t('ekyc.ungDung.taoUngDung')}
                  </Button>
                ) : undefined}
              />
            ) : (
              <DataTable<UngDung>
                caption={t('ekyc.ungDung.bang')}
                columns={cot}
                dataSource={items}
                pagination={false}
                onRow={(u) => ({
                  onClick: () => setChon(u.id),
                  className: u.id === chon ? 'cursor-pointer bg-primary-50' : 'cursor-pointer',
                  tabIndex: 0,
                  'aria-label': t('ekyc.ungDung.xemSoLieuCua', { ten: u.ten }),
                  'aria-selected': u.id === chon,
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setChon(u.id);
                    }
                  },
                })}
              />
            )}
          </CardBody>
        </Card>

        {dangXem && <ChiTietUngDung key={dangXem.id} ungDung={dangXem} />}

        <Card>
          <CardHeader>
            <CardTitle>{t('ekyc.ungDung.tichHopTieuDe')}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-content-secondary">{t('ekyc.ungDung.tichHopKhoaApi')}</p>
              <ul className="space-y-1">
                {QUYEN_KHOA_API_DOI_TAC.map((q) => (
                  <li key={q.ma} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                    <code className="rounded bg-subtle px-1.5 py-0.5 font-mono text-xs text-content-primary">{q.ma}</code>
                    <span className="text-content-secondary">{t(q.khoa)}</span>
                  </li>
                ))}
              </ul>
              <Button variant="outline" size="sm" icon={<KeyOutlined />} onClick={() => dieuHuong('/he-thong/khoa-api')}>
                {t('ekyc.ungDung.moKhoaApi')}
              </Button>
            </div>
            <div className="space-y-2 border-t border-default pt-4">
              <p className="text-sm text-content-secondary">{t('ekyc.ungDung.tichHopWebhook')}</p>
              <pre className="overflow-x-auto rounded bg-subtle p-3 font-mono text-xs leading-relaxed text-content-primary">
                {MAU_KIEM_CHU_KY}
              </pre>
              <p className="text-xs text-content-muted">{t('ekyc.ungDung.tichHopThuLai')}</p>
            </div>
          </CardBody>
        </Card>
      </div>

      {form && (
        <FormUngDung
          open
          ungDung={form.ungDung}
          dangGui={ghi.tao.isPending || ghi.sua.isPending}
          onClose={() => setForm(null)}
          onGui={guiForm}
        />
      )}

      <HopBiMatWebhook vuaCap={vuaCap} onClose={() => setVuaCap(null)} />

      <HopXacNhanThaoTac
        open={hoi !== null}
        tieuDe={hoi?.loai === 'xoay'
          ? t('ekyc.ungDung.xoayHoi', { ten: hoi.ungDung.ten })
          : t('ekyc.ungDung.tatHoi', { ten: hoi?.ungDung.ten ?? '' })}
        than={hoi?.loai === 'xoay' ? t('ekyc.ungDung.xoayThan') : t('ekyc.ungDung.tatThan')}
        nhanXacNhan={hoi?.loai === 'xoay' ? t('ekyc.ungDung.xoayBiMat') : t('ekyc.ungDung.tatUngDung')}
        nguyHiem
        dangChay={ghi.xoay.isPending || ghi.batTat.isPending}
        loi={loiHoi ? dichLoi(loiHoi) : null}
        onClose={() => { setHoi(null); ghi.xoay.reset(); ghi.batTat.reset(); }}
        onXacNhan={xacNhan}
      />
    </div>
  );
}
